import { useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { api, type AuthLecturerResp, type AuthMentorBookingResp, type AuthMentorCourseResp } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AvatarUpload, LanguageMultiSelect, RichTextEditor, TagInput } from './components/ProfileWidgets';

type MentorTab = 'profile' | 'bookings' | 'courses';

const compactCsv = (value?: string | null) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .join(',');

const TIMEZONE_OPTIONS = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Taipei',
  'UTC',
  'America/Los_Angeles',
  'America/New_York',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo'
];

const bookingStatusText = (status?: number) => {
  if (status === 5) return '已完成';
  if (status === 4) return '已取消';
  if (status === 3) return '已確認';
  if (status === 2) return '待確認';
  return '待選時間';
};

const orderStatusText = (status?: number) => {
  if (status === 2) return '支付成功';
  if (status === 1) return '待支付';
  return '已關閉';
};

const MentorAccountPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<MentorTab>('profile');
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState<AuthLecturerResp | null>(null);
  const [bookings, setBookings] = useState<AuthMentorBookingResp[]>([]);
  const [courses, setCourses] = useState<AuthMentorCourseResp[]>([]);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [bookingDetailLoading, setBookingDetailLoading] = useState(false);
  const [confirmingBooking, setConfirmingBooking] = useState(false);
  const [bookingDetail, setBookingDetail] = useState<AuthMentorBookingResp | null>(null);
  const handleMagneticMove = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;
    target.style.setProperty('--tx', `${x * 0.06}px`);
    target.style.setProperty('--ty', `${y * 0.08}px`);
  };

  const handleMagneticLeave = (event: ReactMouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget;
    target.style.setProperty('--tx', '0px');
    target.style.setProperty('--ty', '0px');
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [profileResp, bookingResp, courseResp] = await Promise.all([
        api.getMyLecturerProfile(),
        api.getMentorBookings({ pageCurrent: 1, pageSize: 50 }),
        api.getMentorCourses({ pageCurrent: 1, pageSize: 50 })
      ]);
      setProfile(profileResp);
      setBookings(bookingResp.list || []);
      setCourses(courseResp.list || []);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => setSuccess(''), 5000);
    return () => window.clearTimeout(timer);
  }, [success]);

  const openBookingDetail = async (id: string | number) => {
    setBookingModalVisible(true);
    setBookingDetailLoading(true);
    setBookingDetail(null);
    try {
      const detail = await api.getMentorBookingDetail(id);
      setBookingDetail(detail);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '載入預約詳情失敗');
      setBookingModalVisible(false);
    } finally {
      setBookingDetailLoading(false);
    }
  };

  const onConfirmBooking = async () => {
    if (!bookingDetail?.id) {
      return;
    }
    setConfirmingBooking(true);
    setError('');
    setSuccess('');
    try {
      await api.mentorConfirmBooking({ id: bookingDetail.id });
      setSuccess('預約已確認');
      setBookingDetail({ ...bookingDetail, bookingStatus: 3, confirmNote: '導師已確認預約' });
      setBookings((current) =>
        current.map((item) =>
          String(item.id) === String(bookingDetail.id)
            ? { ...item, bookingStatus: 3, confirmNote: '導師已確認預約' }
            : item
        )
      );
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '確認預約失敗');
    } finally {
      setConfirmingBooking(false);
    }
  };

  const onSaveProfile = async () => {
    if (!profile) return;
    setSavingProfile(true);
    setError('');
    setSuccess('');
    try {
      await api.updateMyLecturerProfile({
        lecturerName: profile.lecturerName,
        lecturerMobile: profile.lecturerMobile,
        lecturerPosition: profile.lecturerPosition,
        lecturerHead: profile.lecturerHead,
        introduce: profile.introduce,
        introduceVideo: profile.introduceVideo,
        tags: compactCsv(profile.tags),
        languages: compactCsv(profile.languages),
        timezone: profile.timezone,
        expertiseAreas: compactCsv(profile.expertiseAreas),
        canBook: profile.canBook ?? 0
      });
      await refreshUser();
      setSuccess('導師資料已更新');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '儲存失敗');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top_right,_#dbeafe_0%,_#f8fafc_38%,_#ecfeff_100%)] account-static"
    >
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <section className="bg-white/70 backdrop-blur-xl border-b border-white/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
            <h1 className="premium-title text-2xl font-bold bg-clip-text text-transparent bg-[linear-gradient(120deg,#0f172a_0%,#0369a1_40%,#7c3aed_100%)]">導師個人中心</h1>
            <p className="text-sm text-slate-500 mt-1">歡迎回來，{user?.nickname || user?.email || '導師'}</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-5">
            <aside className="premium-frame premium-frame--sidebar lg:sticky lg:top-24 h-fit rounded-3xl bg-white/70 backdrop-blur-xl shadow-[0_18px_40px_-24px_rgba(15,23,42,0.5)] p-3">
              <div className="mb-2 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-600/90">Mentor Space</p>
                <p className="text-xs text-slate-500 mt-1">管理預約、課程與導師資料</p>
              </div>
              <div className="space-y-1">
                <button onClick={() => setActiveTab('profile')} onMouseMove={handleMagneticMove} onMouseLeave={handleMagneticLeave} className={`account-side-btn w-full flex items-center gap-3 px-3 py-3 rounded-2xl border text-sm font-semibold transition-all ${activeTab === 'profile' ? 'is-active text-white border-transparent bg-[linear-gradient(120deg,#0ea5e9_0%,#2563eb_55%,#7c3aed_100%)] shadow-[0_10px_24px_-12px_rgba(59,130,246,0.85)]' : 'text-slate-600 border-transparent hover:text-slate-900 hover:bg-slate-50/90'}`}><i className={`ri-id-card-line text-base ${activeTab === 'profile' ? 'text-white' : 'text-cyan-600'}`} />基礎資料<span className="menu-shimmer" /></button>
                <button onClick={() => setActiveTab('bookings')} onMouseMove={handleMagneticMove} onMouseLeave={handleMagneticLeave} className={`account-side-btn w-full flex items-center gap-3 px-3 py-3 rounded-2xl border text-sm font-semibold transition-all ${activeTab === 'bookings' ? 'is-active text-white border-transparent bg-[linear-gradient(120deg,#0ea5e9_0%,#2563eb_55%,#7c3aed_100%)] shadow-[0_10px_24px_-12px_rgba(59,130,246,0.85)]' : 'text-slate-600 border-transparent hover:text-slate-900 hover:bg-slate-50/90'}`}><i className={`ri-calendar-event-line text-base ${activeTab === 'bookings' ? 'text-white' : 'text-cyan-600'}`} />我的預約<span className={`ml-auto text-[11px] px-1.5 py-0.5 rounded-full ${activeTab === 'bookings' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{bookings.length}</span><span className="menu-shimmer" /></button>
                <button onClick={() => setActiveTab('courses')} onMouseMove={handleMagneticMove} onMouseLeave={handleMagneticLeave} className={`account-side-btn w-full flex items-center gap-3 px-3 py-3 rounded-2xl border text-sm font-semibold transition-all ${activeTab === 'courses' ? 'is-active text-white border-transparent bg-[linear-gradient(120deg,#0ea5e9_0%,#2563eb_55%,#7c3aed_100%)] shadow-[0_10px_24px_-12px_rgba(59,130,246,0.85)]' : 'text-slate-600 border-transparent hover:text-slate-900 hover:bg-slate-50/90'}`}><i className={`ri-book-open-line text-base ${activeTab === 'courses' ? 'text-white' : 'text-cyan-600'}`} />我的課程<span className={`ml-auto text-[11px] px-1.5 py-0.5 rounded-full ${activeTab === 'courses' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{courses.length}</span><span className="menu-shimmer" /></button>
              </div>
            </aside>

            <main className="premium-frame premium-frame--content rounded-3xl bg-white/72 backdrop-blur-xl shadow-[0_24px_55px_-26px_rgba(15,23,42,0.4)] p-3 sm:p-5">
              <div key={activeTab} className="account-content-switch">
              {error ? <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">{error}</div> : null}
              {success ? <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 text-sm">{success}</div> : null}
              {loading ? <div className="text-sm text-slate-500">載入中...</div> : null}

              {!loading && activeTab === 'profile' && profile ? (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-cyan-100 bg-[linear-gradient(120deg,rgba(240,249,255,0.9)_0%,rgba(238,242,255,0.7)_55%,rgba(250,245,255,0.75)_100%)] p-4 sm:p-5">
                    <p className="text-sm font-semibold text-slate-800">導師基礎資料</p>
                    <p className="mt-1 text-xs text-slate-500">登入帳號鎖定不可修改，可更新聯絡手機與導師展示資訊</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold text-slate-500">登入帳號（不可變更）</span>
                      <input
                        disabled
                        value={user?.email || ''}
                        className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm text-slate-500"
                      />
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold text-slate-500">聯絡手機</span>
                      <input
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-sky-400"
                        placeholder="請輸入聯絡手機"
                        value={profile.lecturerMobile || ''}
                        onChange={(e) => setProfile({ ...profile, lecturerMobile: e.target.value })}
                      />
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold text-slate-500">導師名稱</span>
                      <input
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-sky-400"
                        placeholder="請輸入導師名稱"
                        value={profile.lecturerName || ''}
                        onChange={(e) => setProfile({ ...profile, lecturerName: e.target.value })}
                      />
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-xs font-semibold text-slate-500">導師職位</span>
                      <input
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-sky-400"
                        placeholder="例如：資深工程師 / 技術總監"
                        value={profile.lecturerPosition || ''}
                        onChange={(e) => setProfile({ ...profile, lecturerPosition: e.target.value })}
                      />
                    </label>

                    <div className="md:col-span-2">
                      <span className="mb-1.5 block text-xs font-semibold text-slate-500">導師頭像</span>
                      <AvatarUpload value={profile.lecturerHead || ''} onChange={(url) => setProfile({ ...profile, lecturerHead: url })} />
                    </div>

                    <div className="md:col-span-2">
                      <span className="mb-1.5 block text-xs font-semibold text-slate-500">導師標籤</span>
                      <TagInput value={profile.tags || ''} onChange={(next) => setProfile({ ...profile, tags: next })} placeholder="輸入標籤後按 Enter，例如：Solidity" />
                    </div>

                    <div className="md:col-span-2">
                      <span className="mb-1.5 block text-xs font-semibold text-slate-500">專業領域</span>
                      <TagInput value={profile.expertiseAreas || ''} onChange={(next) => setProfile({ ...profile, expertiseAreas: next })} placeholder="輸入領域後按 Enter，例如：智能合約審計" />
                    </div>

                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-2xl border border-slate-200 bg-white p-3">
                        <span className="mb-1.5 block text-xs font-semibold text-slate-500">語言（可多選）</span>
                        <LanguageMultiSelect value={profile.languages || ''} onChange={(next) => setProfile({ ...profile, languages: next })} />
                      </div>
                      <label className="rounded-2xl border border-slate-200 bg-white p-3 space-y-1.5">
                        <span className="text-xs font-semibold text-slate-500">時區</span>
                        <select
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-sky-400"
                          value={profile.timezone || ''}
                          onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                        >
                          <option value="">請選擇時區</option>
                          {TIMEZONE_OPTIONS.map((timezone) => (
                            <option key={timezone} value={timezone}>
                              {timezone}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                          <p className="text-xs font-semibold text-slate-500">預約價格（僅展示）</p>
                          <p className="mt-1 text-sm font-bold text-slate-800">{profile.price ?? 0} / {profile.priceUnit || '60分鐘'}</p>
                        </div>
                        <label className="space-y-1.5 block">
                          <span className="text-xs font-semibold text-slate-500">開放預約</span>
                          <select className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-sky-400" value={String(profile.canBook ?? 0)} onChange={(e) => setProfile({ ...profile, canBook: Number(e.target.value) })}>
                            <option value="1">是</option>
                            <option value="0">否</option>
                          </select>
                        </label>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <span className="mb-1.5 block text-xs font-semibold text-slate-500">導師簡介（富文本）</span>
                      <RichTextEditor value={profile.introduce || ''} onChange={(next) => setProfile({ ...profile, introduce: next })} />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <button disabled={savingProfile} onClick={() => void onSaveProfile()} className="inline-flex items-center rounded-xl bg-[linear-gradient(120deg,#0891b2_0%,#2563eb_50%,#6d28d9_100%)] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_-16px_rgba(37,99,235,0.9)] disabled:opacity-60">{savingProfile ? '儲存中...' : '儲存資料'}</button>
                  </div>
                </div>
              ) : null}

              {!loading && activeTab === 'bookings' ? (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2">預約編號</th>
                        <th className="text-left px-3 py-2">學員</th>
                        <th className="text-left px-3 py-2">預約時間</th>
                        <th className="text-left px-3 py-2">狀態</th>
                        <th className="text-left px-3 py-2">金額</th>
                        <th className="text-left px-3 py-2">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((item) => (
                        <tr key={String(item.id)} className="border-t border-slate-100 hover:bg-slate-50/70 transition cursor-pointer" onClick={() => void openBookingDetail(item.id)}>
                          <td className="px-3 py-2">{item.bookingNo}</td>
                          <td className="px-3 py-2">{item.contactName || '-'}</td>
                          <td className="px-3 py-2">{item.bookingDate || '-'} {item.bookingTime || ''}</td>
                          <td className="px-3 py-2">{bookingStatusText(item.bookingStatus)}</td>
                          <td className="px-3 py-2">{item.consultPrice ?? 0}</td>
                          <td className="px-3 py-2">
                            <button onClick={(e) => { e.stopPropagation(); void openBookingDetail(item.id); }} className="text-cyan-700 hover:text-cyan-800 font-semibold">查看詳情</button>
                          </td>
                        </tr>
                      ))}
                      {bookings.length === 0 ? (
                        <tr><td className="px-3 py-4 text-slate-400" colSpan={6}>暫無預約</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {!loading && activeTab === 'courses' ? (
                <div className="space-y-3">
                  {courses.length ? (
                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                      {courses.map((item) => (
                        <article key={String(item.id)} className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-[0_16px_36px_-28px_rgba(15,23,42,0.55)]">
                          <div className="flex gap-3">
                            <div className="h-20 w-28 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                              {item.courseLogo ? <img src={item.courseLogo} alt={item.courseName || '課程'} className="h-full w-full object-cover" /> : null}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-base font-semibold text-slate-900 truncate">{item.courseName || '未命名課程'}</p>
                              <p className="mt-1 text-xs text-slate-500 line-clamp-2">{item.introduce || '暫無課程簡介'}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{item.categoryName || '未分類'}</span>
                                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{item.courseLevel || '入門'}</span>
                                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{item.courseLanguage || '中文'}</span>
                                <span className={`rounded-full px-2 py-1 ${item.isPutaway === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{item.isPutaway === 1 ? '上架中' : '未上架'}</span>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <div className="text-sm text-slate-700">
                              <span className="font-semibold text-slate-900">¥{item.rulingPrice ?? item.coursePrice ?? 0}</span>
                              {item.coursePrice && item.rulingPrice && item.coursePrice !== item.rulingPrice ? <span className="ml-2 text-xs line-through text-slate-400">¥{item.coursePrice}</span> : null}
                            </div>
                            <button
                              onClick={() => navigate(`/course/${String(item.id)}`)}
                              className="inline-flex items-center rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700 hover:bg-cyan-100"
                            >
                              查看課程詳情
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-400">暫無關聯課程</div>
                  )}
                </div>
              ) : null}
              </div>
            </main>
          </div>
        </section>
      </div>

      {bookingModalVisible ? (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px] flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-3xl border border-white/40 bg-white p-5 sm:p-6 shadow-[0_30px_70px_-30px_rgba(2,132,199,0.5)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-bold text-slate-900">預約詳情</p>
                <p className="mt-1 text-xs text-slate-500">你可以查看完整預約資訊並確認預約</p>
              </div>
              <button onClick={() => setBookingModalVisible(false)} className="h-8 w-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200">
                <i className="ri-close-line" />
              </button>
            </div>

            {bookingDetailLoading ? <div className="py-12 text-center text-sm text-slate-500">載入中...</div> : null}

            {!bookingDetailLoading && bookingDetail ? (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-xs text-slate-500">預約單號</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{bookingDetail.bookingNo}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-xs text-slate-500">支付狀態</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{orderStatusText(bookingDetail.orderStatus)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-xs text-slate-500">預約狀態</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{bookingStatusText(bookingDetail.bookingStatus)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-xs text-slate-500">預約時間</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{bookingDetail.bookingDate || '-'} {bookingDetail.bookingTime || ''} {bookingDetail.bookingTimezone || ''}</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 p-3 sm:p-4">
                  <p className="text-sm font-semibold text-slate-900">預約人資訊</p>
                  <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
                    <p>姓名：{bookingDetail.contactName || '-'}</p>
                    <p>電話：{bookingDetail.contactPhone || '-'}</p>
                    <p className="sm:col-span-2">Email：{bookingDetail.contactEmail || '-'}</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 p-3 sm:p-4">
                  <p className="text-sm font-semibold text-slate-900">預約目的</p>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">{bookingDetail.consultGoal || '未填寫'}</p>
                </div>

                {bookingDetail.confirmNote ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
                    確認備註：{bookingDetail.confirmNote}
                  </div>
                ) : null}

                {bookingDetail.orderStatus === 2 && bookingDetail.bookingStatus !== 3 && bookingDetail.bookingStatus !== 4 && bookingDetail.bookingStatus !== 5 ? (
                  <button
                    disabled={confirmingBooking}
                    onClick={() => void onConfirmBooking()}
                    className="w-full rounded-2xl bg-[linear-gradient(120deg,#0891b2_0%,#2563eb_55%,#6d28d9_100%)] px-5 py-4 text-base font-bold text-white shadow-[0_20px_45px_-22px_rgba(37,99,235,0.85)] disabled:opacity-60"
                  >
                    {confirmingBooking ? '確認中...' : '確認預約'}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
};

export default MentorAccountPage;
